import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box,
  Paper,
  Collapse,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { ratingExpressions } from '../utils/ratingExpression';
import { fetchBusinessData, submitFeedback } from '../utils/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
  transition: 'all 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Logo = styled('img')(({ theme }) => ({
  width: '150px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    marginBottom: '15px',
  },
}));

const HeartRating = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  '& > *': {
    margin: '0 4px',
  },
  [theme.breakpoints.down('sm')]: {
    '& > *': {
      margin: '0 2px',
    },
  },
}));

const HeartIcon = styled(FavoriteIcon)(({ theme }) => ({
  color: theme.palette.heart.main,
  cursor: 'pointer',
  transition: 'transform 0.2s, color 0.2s',
  fontSize: '2.5rem', // Increased size for desktop
  '&:hover': {
    transform: 'scale(1.2)',
    color: theme.palette.heart.dark,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.2rem', // Increased size for mobile
  },
}));

const EmptyHeartIcon = styled(FavoriteBorderIcon)(({ theme }) => ({
  color: theme.palette.heart.main,
  cursor: 'pointer',
  transition: 'transform 0.2s, color 0.2s',
  fontSize: '2.5rem', // Increased size for desktop
  '&:hover': {
    transform: 'scale(1.2)',
    color: theme.palette.heart.main,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.2rem', // Increased size for mobile
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

const ServiceFeedback = () => {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { businessId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadBusinessData = async () => {
      if (businessId) {
        const data = await fetchBusinessData(businessId);
        setBusinessData(data);
        setLoading(false);
      } else {
        console.error('Business ID is missing from the URL');
        setLoading(false);
      }
    };
    loadBusinessData();
  }, [businessId]);

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    try {
      const feedbackData = {
        businessId,
        rating,
        review,
        email: email || undefined
      };
      
      const response = await submitFeedback(feedbackData, businessId);
      
      console.log('Submitting review:', review); // Debug log
      
      navigate('/thank-you', { 
        state: { 
          email, 
          businessName: businessData?.businessName,
          businessId: businessId,
          totalReviews: response.totalReviews,
          shouldPromptGoogleReview: response.shouldPromptGoogleReview,
          googleReviewUrl: response.googleReviewUrl,
          reviewSourceId: response.reviewSourceId,
          review: review,
          businessData: {
            logoUrl: businessData?.logoUrl,
            businessName: businessData?.businessName
          }
        } 
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!businessData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" align="center">
          Error: Unable to load business data. Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4, px: isMobile ? 2 : 3 }}>
      <StyledPaper elevation={3}>
        <Logo src={businessData?.logoUrl} alt={`${businessData?.businessName} Logo`} />
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', fontSize: isMobile ? '1.8rem' : '2.125rem' }}>
          Your Opinion Matters to {businessData?.businessName}!
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary', fontSize: isMobile ? '0.9rem' : '1rem' }}>
          We value your feedback. Help us improve and shape your future experiences!
        </Typography>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Typography variant="subtitle1" align="center" sx={{ mb: 2, fontWeight: 600, color: 'text.primary', fontSize: isMobile ? '1rem' : '1.1rem' }}>
            How would you rate your experience with us?
          </Typography>
          <HeartRating>
            {[1, 2, 3, 4, 5].map((value) => (
              <Box key={value} onClick={() => setRating(value)}>
                {value <= rating ? <HeartIcon /> : <EmptyHeartIcon />}
              </Box>
            ))}
          </HeartRating>
          <Box sx={{ minHeight: '3em', mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, color: 'heart.main', fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {ratingExpressions[rating]}
            </Typography>
          </Box>
          <Collapse in={rating > 0}>
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Share your thoughts"
                placeholder="Your feedback helps us serve you better. What did you love? What can we improve?"
                multiline
                rows={isMobile ? 3 : 4}
                value={review}
                onChange={(e) => setReview(e.target.value)}
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <Typography variant="body1" align="center" sx={{ mt: 3, mb: 2, fontWeight: 600, color: 'primary.main', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Want exclusive offers and perks?
              </Typography>
              <TextField
                fullWidth
                label="Your Email Address"
                type="email"
                placeholder="Enter your email for special promotions!"
                value={email}
                onChange={handleEmailChange}
                margin="normal"
                variant="outlined"
                error={!!emailError}
                helperText={emailError || "We'll send you special deals and early access to new services!"}
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                fullWidth 
                sx={{ 
                  mt: 3, 
                  py: isMobile ? 1 : 1.5, 
                  fontWeight: 600, 
                  fontSize: isMobile ? '0.9rem' : '1.1rem', 
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                  '&:hover': { 
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)' 
                  } 
                }}
                size={isMobile ? "medium" : "large"}
              >
                Submit Feedback & Get Perks!
              </Button>
            </Box>
          </Collapse>
        </form>
      </StyledPaper>
    </Container>
  );
};

export default ServiceFeedback;